import { AttributDataType } from '@comparit/common-api';
import type { MarkedItem } from '@comparit/shared';
import type {
  VergleichAttributInput,
  VergleichResponseMap,
} from '../interfaces/vergleich-input.model';

/** One attribute row, carrying one cell per compared tariff in the tariffs' own order. */
export interface MergedAttribut {
  name: string;
  dataType: string;
  values: VergleichAttributInput[];
  tooltip?: string | null;
}

/** One banded section of the table with its rows. */
export interface MergedAttributGruppierung {
  name: string;
  attribute: MergedAttribut[];
}

/**
 * Turns the per-tariff responses into table rows: groups and attributes are merged by name, and
 * each row collects one cell per tariff in the order the tariffs were marked.
 */
export function mergeVergleichGroups(
  markedItems: Pick<MarkedItem, 'kombinationId'>[],
  response: VergleichResponseMap | null | undefined
): MergedAttributGruppierung[] {
  if (!markedItems?.length || !response) return [];

  // A Vergleich covers one run, so every compared tariff sits under the same berechnungId.
  const firstBerechnungId = Object.keys(response)[0];
  if (!firstBerechnungId) return [];

  const responses = response[firstBerechnungId];
  if (!responses) return [];

  const merged = new Map<string, MergedAttributGruppierung>();

  for (const { kombinationId } of markedItems) {
    const data = responses[kombinationId];
    if (!data) continue;

    for (const group of data.attributGruppierungen ?? []) {
      let groupEntry = merged.get(group.name);
      if (!groupEntry) {
        groupEntry = { name: group.name, attribute: [] };
        merged.set(group.name, groupEntry);
      }

      const attrIndex = new Map(groupEntry.attribute.map((attribut, i) => [attribut.name, i]));

      for (const attribut of group.attribute ?? []) {
        const index = attrIndex.get(attribut.name);
        if (index !== undefined) {
          groupEntry.attribute[index].values.push(attribut);
          continue;
        }

        groupEntry.attribute.push({
          name: attribut.name,
          dataType: attribut.dataType,
          values: [attribut],
          tooltip: attribut.tooltip,
        });
        attrIndex.set(attribut.name, groupEntry.attribute.length - 1);
      }
    }
  }

  return Array.from(merged.values());
}

/** A row differs when the compared tariffs do not all say the same thing. */
export const attributHasDifferences = (attribut: MergedAttribut): boolean => {
  if (attribut.values.length <= 1) return false;

  const normalize = (value: VergleichAttributInput) => {
    // Booleans arrive as true, false or null; null and false read the same to the customer.
    if (attribut.dataType === AttributDataType.Boolean) return !!value.attributaussage;
    // A List keeps its answer in its own field, so comparing `attributaussage` would call every
    // List row identical and hide it behind "Nur Unterschiede".
    if (attribut.dataType === AttributDataType.List) {
      return (value.attributAussage ?? []).map(entry => entry.label).join('|');
    }
    return value.attributaussage;
  };

  const first = normalize(attribut.values[0]);
  return attribut.values.some(value => normalize(value) !== first);
};

export const groupHasDifferences = (group: MergedAttributGruppierung): boolean =>
  group.attribute.some(attributHasDifferences);

export const anyGroupHasDifferences = (groups: MergedAttributGruppierung[]): boolean =>
  groups.some(groupHasDifferences);
